import {
	Close as CancelIcon,
	DeleteOutlined as DeleteIcon,
	Edit as EditIcon,
	Save as SaveIcon,
} from '@mui/icons-material';
import {
	GridActionsCellItem,
	GridColDef,
	GridEditInputCell,
	GridEditSingleSelectCell,
	GridRowId,
	GridRowModes,
} from '@mui/x-data-grid-pro';
import { InvoiceAdjustmentTypeEnum, LineItemEntryTypeEnum } from 'parkchirp-shared/enums';

function lineItemIsNegative(entryType: LineItemEntryTypeEnum) {
	return [
		LineItemEntryTypeEnum.WRITE_OFF,
		LineItemEntryTypeEnum.PAYMENT,
		LineItemEntryTypeEnum.ADJUSTMENT,
		LineItemEntryTypeEnum.LANDLORD_COLLECTED,
		LineItemEntryTypeEnum.TERMINATION_ADJUSTMENT_NEGATIVE,
	].includes(entryType);
}

const AMOUNT_KEYS = ['amount', 'amountTax1', 'amountTax2', 'amountTax3', 'amountTax4'];
function sumAmounts(lineItem) {
	return AMOUNT_KEYS.reduce((total, key) => total + (lineItem[key] || 0), 0);
}

function formatLineItemEntryAmount(valueInCents, lineItemEntryType = null) {
	// avoid showing `$NaN`
	if (typeof valueInCents !== 'number') {
		return '';
	}
	let formattedValue = (Math.abs(valueInCents) / 100).toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
	});
	const lineItemEntryTypeAsEnum = LineItemEntryTypeEnum[lineItemEntryType];
	if (valueInCents < 0 || lineItemIsNegative(lineItemEntryTypeAsEnum)) {
		// wrap the value in parenthesis since it's a negative value
		formattedValue = `(${formattedValue})`;
	}
	return formattedValue;
}

const InvoiceLineItemEntryActions = ({ rowModesModel, rows, setRows, setRowModesModel, deleteHandler }) => {
	const handleEditClick = (id: GridRowId) => () => {
		setRows(
			rows.map((row) => {
				if (row.id === id) {
					return {
						...row,
						amount: row.amount / 100,
						amountTax1: row.amountTax1 / 100,
						amountTax2: row.amountTax2 / 100,
						amountTax3: row.amountTax3 / 100,
						amountTax4: row.amountTax4 / 100,
					};
				}
				return row;
			}),
		);
		setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
	};

	const handleSaveClick = (id: GridRowId) => () => {
		setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
	};

	const handleDeleteClick = (id: GridRowId) => () => {
		setRows(rows.filter((row) => row.id !== id));
		deleteHandler(id);
	};

	const handleCancelClick = (id: GridRowId) => () => {
		const editedRow = rows.find((row) => row.id === id);
		if (editedRow!.isNew) {
			setRows(rows.filter((row) => row.id !== id));
		}
		setRowModesModel({
			...rowModesModel,
			[id]: { mode: GridRowModes.View, ignoreModifications: true },
		});
	};

	const renderActions = ({ id }) => {
		const currentRow = rows.find(({ id: rowId }) => rowId === id);
		const isEditingAllowed = !!currentRow?.isNew;

		if (rowModesModel[id]?.mode === GridRowModes.Edit) {
			return [
				<GridActionsCellItem
					icon={<SaveIcon />}
					label='Save'
					key='save'
					onClick={handleSaveClick(id)}
					showInMenu={false}
					data-cy='save-row-button'
				/>,
				<GridActionsCellItem
					icon={<CancelIcon />}
					label='Cancel'
					key='cancel'
					className='textPrimary'
					onClick={handleCancelClick(id)}
					color='inherit'
					showInMenu={false}
					data-cy='cancel-row-button'
				/>,
			];
		}
		if (isEditingAllowed) {
			// if the current row isn't being edited but we allow
			// rows to be edited, return the below options
			return [
				<GridActionsCellItem
					showInMenu={false}
					icon={<EditIcon />}
					label='Edit'
					key='edit'
					className='textPrimary'
					onClick={handleEditClick(id)}
					color='inherit'
					data-cy='edit-row-button'
				/>,
				<GridActionsCellItem
					icon={<DeleteIcon />}
					label='Delete'
					key='delete'
					onClick={handleDeleteClick(id)}
					color='inherit'
					showInMenu={false}
					data-cy='delete-row-button'
				/>,
			];
		}
		return [];
	};

	return renderActions;
};

const ADJUSTMENT_ACTIONS = Object.keys(InvoiceAdjustmentTypeEnum).map((key) => ({
	label: InvoiceAdjustmentTypeEnum[key],
	value: key,
}));

ADJUSTMENT_ACTIONS.push({
	label: '',
	value: '',
});

const getInvoiceLineItemEntryColumns = ({
	isAdjustingEachLineItem,
	rowModesModel,
	setRowModesModel,
	rows,
	setRows,
	deleteHandler,
}) => {
	const columns: GridColDef[] = [
		{ field: 'createdAt', headerName: 'Transaction Date', flex: 1 },
		{ field: 'applicationDate', headerName: 'Application Date', flex: 1 },
		{
			field: 'type',
			editable: isAdjustingEachLineItem,
			headerName: 'Type',
			flex: 1,
			type: 'singleSelect' as const,
			valueOptions: ADJUSTMENT_ACTIONS,
			renderEditCell: (params) => (
				<GridEditSingleSelectCell {...params} data-cy='adjustment-detail-type-select-menu' />
			),
			renderCell: (params) => {
				const maybeAdjustmentType = InvoiceAdjustmentTypeEnum[params.row.type];
				if (maybeAdjustmentType) {
					return <span>{maybeAdjustmentType}</span>;
				}
				return <span>{params.row.formattedType || params.row.type}</span>;
			},
		},
		{
			field: 'amount',
			editable: isAdjustingEachLineItem,
			headerName: 'Net Amount',
			flex: 1,
			type: 'number' as const,
			renderCell: (params) => formatLineItemEntryAmount(params.row.amount, params.row.type),
			renderEditCell: (params) => (
				<GridEditInputCell
					{...params}
					onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
					inputProps={{
						style: {
							textAlign: 'right',
							padding: '0 8px',
						},
						'data-cy': 'adjustment-detail-net-input',
					}}
				/>
			),
		},
		{
			field: 'amountTax1',
			editable: isAdjustingEachLineItem,
			headerName: 'Tax 1',
			flex: 1,
			type: 'number' as const,
			renderCell: (params) => formatLineItemEntryAmount(params.row.amountTax1, params.row.type),
			renderEditCell: (params) => (
				<GridEditInputCell
					{...params}
					onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
					inputProps={{
						style: {
							textAlign: 'right',
							padding: '0 8px',
						},
						'data-cy': 'adjustment-detail-tax-1-input',
					}}
				/>
			),
		},
		{
			field: 'amountTax2',
			editable: isAdjustingEachLineItem,
			headerName: 'Tax 2',
			flex: 1,
			type: 'number' as const,
			renderCell: (params) => formatLineItemEntryAmount(params.row.amountTax2, params.row.type),
			renderEditCell: (params) => (
				<GridEditInputCell
					{...params}
					onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
					inputProps={{
						style: {
							textAlign: 'right',
							padding: '0 8px',
						},
						'data-cy': 'adjustment-detail-tax-2-input',
					}}
				/>
			),
		},
		{
			field: 'amountTax3',
			editable: isAdjustingEachLineItem,
			headerName: 'Tax 3',
			flex: 1,
			type: 'number' as const,
			renderCell: (params) => formatLineItemEntryAmount(params.row.amountTax3, params.row.type),
			renderEditCell: (params) => (
				<GridEditInputCell
					{...params}
					onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
					inputProps={{
						style: {
							textAlign: 'right',
							padding: '0 8px',
						},
						'data-cy': 'adjustment-detail-tax-3-input',
					}}
				/>
			),
		},
		{
			field: 'amountTax4',
			editable: isAdjustingEachLineItem,
			headerName: 'Tax 4',
			flex: 1,
			type: 'number' as const,
			renderCell: (params) => formatLineItemEntryAmount(params.row.amountTax4, params.row.type),
			renderEditCell: (params) => (
				<GridEditInputCell
					{...params}
					onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
					inputProps={{
						style: {
							textAlign: 'right',
							padding: '0 8px',
						},
						'data-cy': 'adjustment-detail-tax-4-input',
					}}
				/>
			),
		},
		{
			field: 'gross',
			headerName: 'Gross Amount',
			flex: 1,
			renderCell: (params) => formatLineItemEntryAmount(params.row.gross, params.row.type),
			renderEditCell: (params) => (
				<GridEditInputCell
					{...params}
					onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
					inputProps={{
						style: {
							textAlign: 'right',
							padding: '0 8px',
						},
						'data-cy': 'adjustment-detail-gross-input',
					}}
				/>
			),
		},
	];

	if (isAdjustingEachLineItem) {
		const invoiceLineItemEntryActions = InvoiceLineItemEntryActions({
			rowModesModel,
			setRowModesModel,
			rows,
			setRows,
			deleteHandler,
		});
		const result: GridColDef = {
			field: 'actions',
			type: 'actions' as const,
			headerName: 'Actions',
			flex: 1,
			cellClassName: 'actions',
			getActions: invoiceLineItemEntryActions,
		};
		return [result, ...columns];
	}
	return columns;
};

export {
	formatLineItemEntryAmount,
	getInvoiceLineItemEntryColumns,
	InvoiceLineItemEntryActions,
	lineItemIsNegative,
	sumAmounts,
};
